import { TaskStatus } from "./data/models/Tasks";
import { getInnerError } from "./utils/adaya/AdayaUtils";
import { getLogger } from "./utils/logging";
import { arrayFilterRemove, tryJsonStringify } from "./utils/ObjectUtils";

const { log, warn } = getLogger('RequestManager', { color: 'yellow', enabled: false });

export const DEFAULT_REQUEST_OPTIONS = (): ManagedRequestOptions => {
    return {
        ignoreErrors: true,
        forceNewRequest: false,
        clearWhenFinished: true
    };
}

/**
 * @description Utility which can keep track of arbitrary requests based on a unique key
 * (which are generaly just JSON strings of the param data, or otherwise any other unique id).
 * Multiple requests (of the same type, but with different params) can be executed by any client,
 * and it can receive the results with the wrapped request details, so that the client can use/refer
 * ti it within the context of an asynchronous environment.
 * @class RequestManager
 */
export class RequestManager {

    requests: Array<ManagedRequest> = [];

    // Returns existing request if matching key, otherwise starts the request, waits, and returns the result.
    // If retryFinished == true, and it finds an existing request that is finished
    public request = async (
        key: string | {},
        method,
        opts: ManagedRequestOptions): Promise<ManagedRequest> => {
        try {
            //log('request', key, method, opts);
            if (!key) throw "Requests must provide a key to guarantee uniqueness.";
            if (!method) throw "Requests require a method to execute.";

            // ensure opts and key
            opts = Object.assign(DEFAULT_REQUEST_OPTIONS(), opts); // keep defaults
            key = this.makeRequestKey(key);
            let request = this.findRequest(key);

            // start a new request, or restart an errored one, if options allow.
            if (opts.forceNewRequest||
                !request ||
                (request?.status == TaskStatus.Error && opts?.ignoreErrors) ||
                (request?.status == TaskStatus.Finished && opts?.forceNewRequest) ||
                (request?.status == TaskStatus.FinishedWithErrors && (opts?.ignoreErrors || opts?.forceNewRequest)) ||
                (request?.status == TaskStatus.Started && opts?.forceNewRequest)) {
                request = this.createRequest(key, method, opts);
                request = await this.startRequest(request);
            } else {
                if (request.status == TaskStatus.Started) {
                    log("Same request is already in progress...", request);
                } else {
                    // otherwise the request is hanging, how to attach to it? TODO: add listener dictionary?
                    warn("Same request exists, but unknown status?", request);
                }
            }

            return request;
        } catch(e) {
            warn('Exception processing request:', key, opts, e);
            throw e;
        }
    }

    public requestMatchesKey = (request: ManagedRequest, key: string | {}) => {
        if (!request || !request.key || !key) throw `Request does not exist or no key given.`;
        key = this.makeRequestKey(key);
        return request.key == key;
    }

    /**
     * @description Looks for an existing request by unique key.
     * @private
     * @param {*} key
     */
    private findRequest = (key: string | {}) => {
        key = this.makeRequestKey(key);
        return this.requests.find(r => r.key == key);
    }

    /**
    * @description Creates a new ManagedRequest and puts it into the system. Does not start it.
    * @private
    * @param {*} key
    * @param {*} method
    */
    private createRequest = (key: string, method, opts?: any): ManagedRequest => {
        log('createRequest', key, method, opts);
        const request = new ManagedRequest(key, method, opts);
        this.requests.push(request);
        return request;
    }

    /**
     * @description Starts the request, and handles the response.
     * @private
     * @param {*} key
     * @param {*} method
     */
    private startRequest = async (request: ManagedRequest): ManagedResponse => {
        log('startRequest', request);
        request.startedAt = new Date();
        request.status = RequestStatus.STARTED;
        request.response = request.method ? await request.method() : undefined;
        return this.onRequestFinished(request);
    }

    /**
     * @description Handles the request's response from the server.
     * @private
     * @param {ManagedRequest} request
     */
    private onRequestFinished = (request: ManagedRequest): ManagedRequest => {
        log('onRequestFinished', request);
        request.finishedAt = new Date();
        if (request.response) {
            request.status = RequestStatus.FINISHED;
        } else {
            request.status = RequestStatus.ERROR;
            request.error = getInnerError(r);
        }

        if (request.options.clearWhenFinished) {
            this.deleteRequest(request.key);
        }

        return request;
    }

    public deleteAllRequests = (): Array<ManagedRequest> => {
        this.requests = [];
    }

    /**
     * @description Deletes a given request from the system by the key.
     * @private
     * @param {*} request
     */
    public deleteRequest = (key: string | {} | ManagedRequest): Array<ManagedRequest> => {
        log(`deleteRequest`, key)
        if (!key) throw "No request or key given to deleteRequest.";
        this.requests = arrayFilterRemove(this.requests, (r) => {
            return (typeof key == 'string') ? key : r.key == key
        });
        return this.requests;
    }

    /**
     * @description Deletes a given request from the system by the key.
     * @private
     * @param {*} request
     */
    private deleteRequestByKey = (key): Array<ManagedRequest> => {
        this.requests = arrayFilterRemove(this.requests, (r) => r.key == key);
        return this.requests;
    }

    /**
     * @description Generates a "unique" string key based on the given parameters, which can be
     * either a string (already keyed), or an object, and will return a string of it.
     * @param {*} params
     */
    public makeRequestKey = (params: string | {}): string => {
        return tryJsonStringify(params, Date.now());
    }

}

/**
 * @description Contains metadata information about a given request.
 * @export
 * @class Request
 */
export class ManagedRequest {
    public key: string;
    public method: () => any;
    public options: ManagedRequestOptions;

    public startedAt: Date;
    public finishedAt: Date | undefined;
    public status: RequestStatus;

    public success: boolean;
    public response: any;
    public error: any;

    constructor(key = '', method, options = {}) {
        this.key = key;
        this.method = method;
        this.options = options;
        this.status = RequestStatus.CREATED;
    }
}

export type ManagedRequestOptions = {
    ignoreErrors?: boolean;
    forceNewRequest?: boolean;
    clearWhenFinished: boolean;
    params?: any; // can pass the request params through
}

// Signifies standard API responses from our server, which all endpoints should follow.
export type ApiResponse = {
    success: boolean;
    data: any;
    error: any;
}

export enum RequestStatus {
    CREATED = "created",
    STARTED = "started",
    FINISHED = "finished",
    ERROR = "error"
}